import React, { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import RouteSuspense from "../../components/layout/RouteSuspense";
import AppContextBar from "../../components/layout/AppContextBar";
import ProductSwitcher from "../../components/layout/ProductSwitcher";
import AccountMenu from "../../components/layout/AccountMenu";
import { useLanguage } from "../../components/ui/LanguageProvider";
import CrmRuntimeTranslator from "../crm/CrmRuntimeTranslator";
import { useMenuRoles } from "../../components/auth/MenuRoleContext";
import AccessDeniedNotice from "../../components/auth/AccessDeniedNotice";
import FeatureStatusBadge from "../crm/components/FeatureStatusBadge";
import useIsMobile from "../../components/ui/useIsMobile";
import MobileApp from "../../mobile/MobileApp";
import { SidebarSlot, SidebarBackdrop, mobileAsideStyle } from "../../components/layout/mobileSidebar";
import type { FeatureStatus } from "../crm/components/FeatureStatusBadge";
import type { Role } from "../../components/auth/AuthContext";
import { applyMenuOverrides } from "../../data/menuOverrides";
import { itemsMovedAwayFrom, sectionLabelOf } from "../../data/menuPlacement";
import { resolveMenuLabel } from "../../data/menuLabels";
import { renderMenuIcon } from "../../data/menuIcons";
import { PageMetaProvider, pageTitleOf } from "../../components/layout/PageMetaContext";
import type { Crumb, PageMeta } from "../../components/layout/PageMetaContext";
import { AUTOMATION_MENU_LABEL_EN } from "../../data/menuLabelsEn";
import { isMenuSearchShortcut, MenuSearchInput, SIDE_ITEM_HOVER_CLASS, sideItemIconColor, sideItemStyle, sideSectionLabelStyle } from "../../components/layout/sidebarShared";
import {
  BookOpen,
  Boxes,
  Calculator,
  ChevronDown,
  ClipboardList,
  Database,
  FileAudio,
  FileSignature,
  Headset,
  ListChecks,
  Map,
  MessageSquare,
  FileText,
  FlaskConical,
  PanelLeftClose,
  PanelLeftOpen,
  ReceiptText,
  Send,
  Store,
  Warehouse,
  Zap, Dot,} from "lucide-react";

type SideMenuItem = {
  key: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  /** 노출 최소 role. 생략 시 viewer (guest는 default-deny) — 1-b에서 DB화 예정 */
  minRole?: Role;
  /** 라벨 옆 상태 배지. 생략하면 배지 없음(= 기존 항목 그대로). */
  status?: FeatureStatus;
  /** 권한 키 — applyMenuOverrides 가 붙인다(다른 섹션·대메뉴에서 옮겨 온 메뉴는 조합 키와 다르다) */
  menuKey?: string;
  /** 정확히 이 주소일 때만 활성 — 아래 주소(/x/y)를 가진 형제 메뉴가 있을 때 둘 다 켜지지 않게 */
  end?: boolean;
};

type SideMenuSection = {
  key: string;
  label: string;
  items: SideMenuItem[];
  /** 노출 최소 role. 생략 시 viewer — 섹션 단위 게이트 */
  minRole?: Role;
  /** 권한 키 — applyMenuOverrides 가 붙인다(CRM 에서 옮겨 온 섹션은 crm:…) */
  menuKey?: string;
};

// 권한 키 — 오버라이드가 붙인 실제 키 우선. 조합 키는 옮겨 온 메뉴에서 틀린다.
const sectionMenuKeyOf = (section: SideMenuSection) => section.menuKey ?? `automation:${section.key}`;
const itemMenuKeyOf = (section: SideMenuSection, item: SideMenuItem) => item.menuKey ?? `automation:${section.key}:${item.key}`;

const automationMenuLabel = (label: string, language: string) => (language === "en" ? AUTOMATION_MENU_LABEL_EN[label] ?? label : label);

const MENU_SECTIONS: SideMenuSection[] = [
  {
    key: "planning-management",
    label: "기획 관리",
    items: [
      { key: "planning-documents", label: "기획 문서 관리", icon: <FileText size={16} />, path: "/planning/documents" },
    ],
  },
 
  {
    // 설비 카탈로그(2026-10-07) — DB 관리 · POC 아래가 아닌 최상위 섹션. 분류 편집은 화면 왼쪽 트리 ⋯ 에서 한다
    key: "equipment-catalog",
    label: "설비 카탈로그",
    items: [
      { key: "equipment-catalog", label: "설비 카탈로그", icon: <Boxes size={16} />, path: "/automation/equipment-catalog" },
    ],
  },
  {
    // 현장 리서치(2026-11-04) — 전시회·세미나·기업 방문 행사별 업체 조사. 화면 주소는 /crm/field-research(사용자 지정)지만
    // 메뉴는 IT-CONSULTING 에 둔다 — App.tsx 의 /crm/field-research 형제 라우트가 이 틀(AreaFrame home="automation")로 연다.
    key: "field-research",
    label: "현장 리서치",
    items: [
      // end — 아래 주소(/questions)를 가진 형제가 있어 「행사」가 같이 켜지지 않게
      { key: "events", label: "행사", icon: <Map size={16} />, path: "/crm/field-research", end: true },
      { key: "questions", label: "질문 관리", icon: <MessageSquare size={16} />, path: "/crm/field-research/questions" },
    ],
  },
  {
    key: "vault",
    label: "Vault",
    minRole: "admin",
    items: [
      { key: "vault-content", label: "관리", icon: <Database size={16} />, path: "/automation/vault/content" },
    ],
  },
  // {
  //   key: "documents",
  //   label: "문서 작성",
  //   items: DOCUMENT_MENU_ITEMS.map((item) => ({ ...item, icon: <FileText size={16} /> })),
  // },
  {
    key: "operations",
    label: "운영 관리",
    minRole: "admin",
    items: [
      { key: "db-mou-status", label: "MOU 현황 관리", icon: <FileText size={16} />, path: "/automation/db/mou-status" },
    ],
  },
  {
    key: "db",
    label: "DB 관리",
    minRole: "admin",
    items: [
      { key: "db-document-data", label: "문서 DB 관리", icon: <Database size={16} />, path: "/automation/db/document-data" },
    ],
  },
  {
    key: "guides",
    label: "가이드",
    items: [
      { key: "diagnosis-logic", label: "진단 로직", icon: <ClipboardList size={16} />, path: "/automation/diagnosis/logic" },
      { key: "invoice-guide", label: "인보이스 가이드", icon: <BookOpen size={16} />, path: "/automation/invoice-guide" },
      { key: "simulator", label: "시뮬레이터", icon: <Zap size={16} />, path: "/automation/simulator" },
    ],
  },
  {
    key: "planning",
    label: "서비스 기획",
    items: [
      { key: "sales-pipeline", label: "Sales Pipeline", icon: <ClipboardList size={16} />, path: "/planning/sales-pipeline" },
      { key: "roi-analysis", label: "ROI 분석 도구", icon: <ClipboardList size={16} />, path: "/planning/roi-analysis" },
    ],
  },
  {
    key: "settlement",
    label: "브랜드 정산",
    minRole: "admin",
    items: [
      { key: "brand501", label: "브랜드501", icon: <ReceiptText size={16} />, path: "/settlement/brand501" },
    ],
  },
  {
    key: "poc",
    label: "POC",
    items: [
      { key: "warehouse-rack", label: "창고 3D Rack", icon: <Warehouse size={16} />, path: "/automation/poc/warehouse-rack" },
      { key: "twin-studio", label: "디지털 트윈 스튜디오 (TEST)", icon: <FlaskConical size={16} />, path: "/automation/poc/twin-studio" },
      { key: "mwms-api-test", label: "MWMS API 테스트", icon: <Send size={16} />, path: "/automation/poc/mwms-api-test" },
      { key: "wms-billing-sim", label: "WMS Billing 시뮬레이터", icon: <Calculator size={16} />, path: "/automation/poc/wms-billing-simulation" },
      { key: "meeting-notes", label: "AI 회의록 자동화", icon: <FileAudio size={16} />, path: "/automation/poc/meeting-notes", status: "beta" },
      { key: "care-desk", label: "Care Desk 접수·스케줄", icon: <Headset size={16} />, path: "/automation/poc/care-desk" },
      { key: "rfq", label: "견적 요청 (RFQ)", icon: <Send size={16} />, path: "/automation/poc/rfq", status: "development" },
      { key: "hwp-form", label: "HWP 서식 편집·출력", icon: <FileSignature size={16} />, path: "/automation/poc/hwp-form", status: "beta" },
      { key: "task-management", label: "Task 관리", icon: <ListChecks size={16} />, path: "/automation/poc/tasks" },
      { key: "proposal", label: "제안서 빌더", icon: <FileText size={16} />, path: "/automation/poc/proposal", status: "development" },
      { key: "mro-arena", label: "MRO 편집샵(Vault)", icon: <Store size={16} />, path: "/crm/poc/mro", status: "development" },
    ],
  },
];

// 1-b: 메뉴 노출은 DB 매핑(menu_role_map) 우선, 없으면 코드 상수(minRole) 폴백 — useMenuRoles().visible

/**
 * 모바일 더보기용 IT-CONSULTING 메뉴 — 이 레이아웃 사이드바와 같은 구조·같은 권한 검사(준비 중 항목 제외).
 */
export function useAutomationMenuGroups(): { key: string; label: string; items: { key: string; label: string; path: string }[] }[] {
  const { visible, overrides } = useMenuRoles();
  const { language } = useLanguage();
  return useMemo(() => {
    const merged = applyMenuOverrides(MENU_SECTIONS, overrides, "automation", <Dot size={16} />, (name) => renderMenuIcon(name, 16), resolveMenuLabel);
    return merged
      .filter((section) => visible(sectionMenuKeyOf(section), section.minRole))
      .map((section) => ({
        key: `automation-${section.key}`,
        label: automationMenuLabel(section.label, language),
        items: (section.items ?? [])
          .filter((item) => visible(itemMenuKeyOf(section, item), item.minRole) && item.status !== "coming_soon")
          .map((item) => ({ key: item.key, label: automationMenuLabel(item.label, language), path: item.path })),
      }))
      .filter((g) => g.items.length > 0);
  }, [overrides, visible, language]);
}

export default function AutomationLayout() {
  const { visible, overrides } = useMenuRoles();
  const { language } = useLanguage();
  const routeLocation = useLocation();
  // 경로별 접근 가드 — 현재 경로가 속한 섹션의 권한 재검사(상속 적용). 매핑 없는 경로는 영역 진입 권한으로 허용.
  // 메뉴 관리(/settings/menus)에서 바꾼 이름·링크·소속·순서를 코드 카탈로그에 입힌 결과.
  // 오버라이드가 없으면 MENU_SECTIONS 그대로다.
  const sections = useMemo(
    // CRM 에서 옮겨 온 메뉴는 CRM 사이드바와 같은 이름(번역 맵)으로 — resolveMenuLabel.
    () => {
      const merged = applyMenuOverrides(MENU_SECTIONS, overrides, "automation", <Dot size={16} />, (name) => renderMenuIcon(name, 16), resolveMenuLabel);
      if (language !== "en") return merged;
      return merged.map((section) => ({
        ...section,
        label: automationMenuLabel(section.label, language),
        items: section.items?.map((it) => ({ ...it, label: automationMenuLabel(it.label, language) })),
      }));
    },
    [overrides, language]
  );
  // CRM 으로 옮겨 간 IT-CONSULTING 메뉴 — 사이드바엔 없지만 화면(경로)은 여기서 열린다.
  const movedAway = useMemo(() => itemsMovedAwayFrom("automation", overrides), [overrides]);
  // 권한 요청 버튼이 어떤 메뉴에 대한 요청인지 알아야 해서 boolean 대신 차단된 메뉴 키를 돌려준다.
  const deniedKey = useMemo(() => {
    let bestKey: string | null = null;
    let bestFallback: Role | undefined;
    let bestLen = -1;
    for (const section of sections) {
      for (const it of section.items ?? []) {
        if (routeLocation.pathname.startsWith(it.path) && it.path.length > bestLen) {
          // CRM 에서 옮겨 온 메뉴는 그 메뉴 키로 — 섹션만 보면 CRM 쪽 항목 등급(admin 등)이 빠진다.
          const movedIn = !!it.menuKey && !it.menuKey.startsWith("automation:");
          bestKey = movedIn ? it.menuKey! : sectionMenuKeyOf(section);
          bestFallback = movedIn ? it.minRole : section.minRole;
          bestLen = it.path.length;
        }
      }
    }
    // CRM 으로 옮겨 간 메뉴의 화면 — 그 메뉴 키로 재검사(옮긴 자리의 상속 포함). 빠지면 URL 로 그냥 열린다.
    for (const m of movedAway) {
      if (routeLocation.pathname.startsWith(m.path) && m.path.length > bestLen) {
        bestKey = m.menuKey;
        bestFallback = m.minRole as Role | undefined;
        bestLen = m.path.length;
      }
    }
    return bestKey && !visible(bestKey, bestFallback) ? bestKey : null;
  }, [routeLocation.pathname, visible, sections, movedAway]);
  const accessDenied = deniedKey !== null;
  // 작은 화면에선 사이드바를 접힌(아이콘) 상태로 시작 — 모바일에서 본문 폭 확보
  const [collapsed, setCollapsed] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 1024
  );
  // 폰(≤768px): 사이드바를 본문 위 오버레이로 띄운다(CRM 과 동일 패턴).
  // 흐름에 남겨두면 펼쳤을 때 220px 을 본문에서 빼앗아 글을 읽을 수 없다.
  const isMobile = useIsMobile();
  // 모바일 웹앱 — 767px 이하는 CRM 과 같은 모바일 셸(앱바 + 하단 탭)로 감싼다. 본문은 같은 화면.
  const isPhone = useIsMobile(767);
  // CRM 사이드바와 같은 규칙 — 지금 화면이 속한 섹션만 펼치고 나머지는 접은 채 시작한다.
  //   사용자가 펼치고 접은 상태는 이 브라우저에 남는다. docs/ux-laws/HICKS_LAW.md 규칙 6 · JAKOBS_LAW.md 규칙 4
  const activeSectionKey = useMemo(() => {
    let key: string | null = null;
    let len = -1;
    for (const s of sections) {
      for (const it of s.items ?? []) {
        const p = routeLocation.pathname;
        if ((p === it.path || p.startsWith(it.path + "/")) && it.path.length > len) { key = s.key; len = it.path.length; }
      }
    }
    return key;
  }, [routeLocation.pathname, sections]);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(ITC_SECTIONS_KEY);
      const arr = raw ? JSON.parse(raw) : null;
      if (Array.isArray(arr)) return new Set(arr.filter((k) => typeof k === "string"));
    } catch { /* storage 차단 — 기본값 */ }
    return new Set(MENU_SECTIONS.map((s) => s.key));
  });
  useEffect(() => {
    if (!activeSectionKey) return;
    setCollapsedSections((prev) => {
      if (!prev.has(activeSectionKey)) return prev;
      const next = new Set(prev);
      next.delete(activeSectionKey);
      return next;
    });
  }, [activeSectionKey]);
  useEffect(() => {
    try { localStorage.setItem(ITC_SECTIONS_KEY, JSON.stringify(Array.from(collapsedSections))); } catch { /* 무시 */ }
  }, [collapsedSections]);
  const [navQuery, setNavQuery] = useState("");
  // 메뉴 검색 단축키(Cmd/Ctrl+K) — CRM 사이드바와 같다. 접혀 있으면 펼친 뒤 포커스. 다른 키 입력은 건드리지 않는다.
  const navSearchRef = useRef<HTMLInputElement>(null);
  const focusSearchPending = useRef(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (!isMenuSearchShortcut(e)) return;
      e.preventDefault();
      if (navSearchRef.current) {
        navSearchRef.current.focus();
        navSearchRef.current.select();
        return;
      }
      focusSearchPending.current = true;
      setCollapsed(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (!collapsed && focusSearchPending.current) {
      focusSearchPending.current = false;
      navSearchRef.current?.focus();
    }
  }, [collapsed]);
  // 상단 컨텍스트 바 좌측 = 경로 "IT-CONSULTING / {섹션}"(DESIGN-AI §1). 매칭 실패 시 영역 이름만.
  // 페이지 헤더 제목(DESIGN-AI §19) = 메뉴 관리의 "페이지 제목" || 메뉴 이름 — PageMetaProvider 로 내려준다.
  const pageMeta = useMemo<PageMeta | null>(() => {
    const path = routeLocation.pathname;
    let best: PageMeta | null = null;
    let bestLen = -1;
    for (const s of sections) {
      for (const it of s.items ?? []) {
        if ((path === it.path || path.startsWith(it.path + "/")) && it.path.length > bestLen) {
          // 경로 칸은 눌러서 이동 — 섹션 → 섹션 첫 메뉴, 상세 화면(메뉴 경로 아래)이면 목록 메뉴 칸을 덧붙인다.
          const crumbs: Crumb[] = [{ label: "IT-CONSULTING", to: sections[0]?.items?.[0]?.path }, { label: s.label, to: s.items?.[0]?.path }];
          if (path !== it.path) crumbs.push({ label: it.label, to: it.path });
          best = { menuLabel: it.label, title: pageTitleOf(it.label, (it as { pageTitle?: string }).pageTitle), crumbs, icon: (it as { icon?: React.ReactNode }).icon };
          bestLen = it.path.length;
        }
      }
    }
    for (const m of movedAway) {
      if ((path === m.path || path.startsWith(m.path + "/")) && m.path.length > bestLen) {
        const crumbs: Crumb[] = [{ label: "CRM", to: "/crm/dashboard" }, { label: sectionLabelOf(m.sectionKey, overrides, resolveMenuLabel) }];
        if (path !== m.path) crumbs.push({ label: m.label, to: m.path });
        best = { menuLabel: m.label, title: pageTitleOf(m.label, m.pageTitle), crumbs, icon: renderMenuIcon(m.icon, 16) };
        bestLen = m.path.length;
      }
    }
    return best;
  }, [routeLocation.pathname, sections, movedAway, overrides]);
  const pageLabel = pageMeta?.menuLabel ?? "";

  if (isPhone) {
    return (
      <MobileApp
        page={<PageMetaProvider value={pageMeta}><RouteSuspense><Outlet /></RouteSuspense></PageMetaProvider>}
        pageMeta={pageMeta}
        accessDenied={accessDenied}
        deniedNotice={<AccessDeniedNotice menuKey={deniedKey} menuLabel={pageLabel} />}
        groups={[]}
      />
    );
  }

  function toggleSection(key: string) {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  return (
    <div className="app-layout-shell" style={{ ...layoutStyle, position: "relative" }}>
      {/* 폰: aside 가 오버레이가 되므로 아이콘 레일 폭을 슬롯으로 예약(리플로우 방지) */}
      {isMobile ? <SidebarSlot /> : null}
      {isMobile && !collapsed ? <SidebarBackdrop onClose={() => setCollapsed(true)} /> : null}
      {/* 사이드바 메뉴 라벨만 언어 대응 — 문서 편집 본문(Outlet)은 원문 유지(VAU-22) */}
      <CrmRuntimeTranslator language={language}>
      <aside
        style={{
          ...sidebarStyle,
          // CRM 사이드바와 같은 폭(240 / 접힘 56).
          width: collapsed ? 56 : 240,
          minWidth: collapsed ? 56 : 240,
          ...(mobileAsideStyle(isMobile, !collapsed) ?? {}),
        }}
      >
        <div
          style={{
            ...sidebarHeaderStyle,
            flexDirection: "column",
            alignItems: "stretch",
            gap: 8,
            justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? "14px 0" : "12px 12px 10px",
          }}
        >
          {!collapsed ? (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                <ProductSwitcher current="itc" />
                <button type="button" onClick={() => setCollapsed((v) => !v)} title="메뉴 접기" style={collapseButtonStyle} className="hover:bg-surface-hover">
                  <PanelLeftClose size={16} />
                </button>
              </div>
              <MenuSearchInput
                inputRef={navSearchRef}
                value={navQuery}
                onChange={setNavQuery}
                placeholder={language === "en" ? "Search menu" : "메뉴 검색"}
              />
            </>
          ) : (
            <button
              type="button"
              onClick={() => setCollapsed((v) => !v)}
              title="메뉴 펼치기"
              style={collapseButtonStyle}
              className="hover:bg-surface-hover"
            >
              <PanelLeftOpen size={16} />
            </button>
          )}
        </div>

        <nav
          style={navStyle}
          className="app-scrollbar"
          onClick={(e) => {
            // 폰: 메뉴(링크) 선택 후 오버레이를 닫아 본문을 바로 보여준다.
            // 섹션 접기 버튼 클릭은 닫지 않는다 — 링크(a)일 때만.
            if (isMobile && (e.target as HTMLElement).closest("a")) setCollapsed(true);
          }}
        >
          {!collapsed && navQuery.trim() ? (() => {
            const qq = navQuery.trim().toLowerCase();
            const flat = sections
              .filter((section) => visible(sectionMenuKeyOf(section), section.minRole))
              .flatMap((section) => section.items.filter((item) => visible(itemMenuKeyOf(section, item), item.minRole)));
            const matched = flat.filter((item) => item.label.toLowerCase().includes(qq) || item.key.toLowerCase().includes(qq));
            return matched.length
              ? matched.map((item) => <SideMenuItemButton key={item.key} item={item} collapsed={false} />)
              : <div style={{ padding: "12px 16px", fontSize: 12, color: "var(--app-text-muted)" }}>검색 결과 없음</div>;
          })() : sections.filter((section) => visible(sectionMenuKeyOf(section), section.minRole)).map((section) => {
            const isSectionCollapsed = collapsedSections.has(section.key);
            return (
              <div key={section.key} style={{ marginBottom: 2 }}>
                {!collapsed ? (
                  <button
                    type="button"
                    onClick={() => toggleSection(section.key)}
                    style={sectionButtonStyle}
                  >
                    <span style={sectionLabelStyle}>{section.label}</span>
                    <ChevronDown
                      size={12}
                      style={{
                        transform: isSectionCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                        transition: "transform 0.15s",
                      }}
                    />
                  </button>
                ) : null}

                {(!isSectionCollapsed || collapsed) &&
                  section.items.filter((item) => visible(itemMenuKeyOf(section, item), item.minRole)).map((item) => (
                    <SideMenuItemButton key={item.key} item={item} collapsed={collapsed} />
                  ))}
              </div>
            );
          })}
        </nav>
        {/* 접힌 메뉴(56px)에서도 계정은 남긴다 — 아바타만. */}
        <AccountMenu variant={collapsed ? "compact" : "sidebar"} />
      </aside>
      </CrmRuntimeTranslator>

      <main style={mainStyle} className="app-scrollbar">
        <AppContextBar crumbs={pageMeta?.crumbs ?? ["IT-CONSULTING"]} />
        {accessDenied ? (
          <AccessDeniedNotice menuKey={deniedKey} menuLabel={pageLabel} />
        ) : (
          <PageMetaProvider value={pageMeta}><RouteSuspense><Outlet /></RouteSuspense></PageMetaProvider>
        )}
      </main>
    </div>
  );
}

function SideMenuItemButton({
  item,
  collapsed,
}: {
  item: SideMenuItem;
  collapsed: boolean;
}) {
  return (
    <NavLink
      to={item.path}
      end={item.end}
      title={collapsed ? item.label : undefined}
      // CRM 사이드바와 같은 모양(sidebarShared). 움직이는 막대가 없어 펼친 상태도 왼쪽 안쪽 선으로 활성을 표시한다.
      className={({ isActive }) => (isActive ? "active" : SIDE_ITEM_HOVER_CLASS)}
      style={({ isActive }) => ({ ...sideItemStyle(isActive, collapsed), boxShadow: isActive ? "inset 3px 0 0 var(--primary)" : "none" })}
    >
      {({ isActive }) => (
        <>
          <span style={{ flexShrink: 0, display: "flex", color: sideItemIconColor(isActive) }}>{item.icon}</span>
          {!collapsed ? (
            <>
              <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {item.label}
              </span>
              {item.status ? <FeatureStatusBadge status={item.status} size="sm" /> : null}
            </>
          ) : null}
        </>
      )}
    </NavLink>
  );
}

const layoutStyle: React.CSSProperties = {
  display: "flex",
  // 높이는 .app-layout-shell(CSS)에서 100dvh 폴백과 함께 지정 — 모바일 주소창 대응
  overflow: "hidden",
  background: "var(--app-bg)",
};

const sidebarStyle: React.CSSProperties = {
  maxWidth: 240,
  transition: "width 0.2s ease, min-width 0.2s ease",
  background: "var(--app-surface)",
  borderRight: "1px solid var(--hairline)",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  flexShrink: 0,
  position: "relative",
};

const sidebarHeaderStyle: React.CSSProperties = {
  borderBottom: "1px solid var(--hairline)",
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
};

// CRM 사이드바 접기 버튼과 같은 32px 고스트(hover 면은 className).
const collapseButtonStyle: React.CSSProperties = {
  border: "none",
  borderRadius: 6,
  cursor: "pointer",
  color: "var(--ink-muted)",
  width: 32,
  height: 32,
  padding: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  alignSelf: "center",
};

const navStyle: React.CSSProperties = {
  flex: 1,
  overflowY: "auto",
  overflowX: "hidden",
  padding: "8px 0",
};

const sectionButtonStyle: React.CSSProperties = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: 30,
  padding: "0 14px 0 16px",
  background: "transparent",
  border: "none",
  cursor: "pointer",
  color: "var(--ink-muted)",
  fontFamily: "inherit",
};

// 섹션 제목 표기는 CRM 사이드바와 같게(대문자 변환 없음) — docs/ux-laws/JAKOBS_LAW.md 규칙 4
//   Vault UI: 12px/600 ink-muted — sidebarShared 공용 값.
const sectionLabelStyle: React.CSSProperties = sideSectionLabelStyle;

const ITC_SECTIONS_KEY = "itc-sidebar-collapsed-sections";

const mainStyle: React.CSSProperties = {
  flex: 1,
  minWidth: 0,
  overflowY: "auto",
  // 모바일에서 A4(210mm)·넓은 표 등 고정폭 콘텐츠가 잘리지 않도록 가로 스크롤 허용
  // (데스크톱은 폭이 충분해 스크롤바가 나타나지 않음)
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  display: "flex",
  flexDirection: "column",
};